import { Box, Container, Group, Text, Title } from '@mantine/core';
import { useKiosk } from '@/providers/kioskState';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { OfflineBanner } from './OfflineBanner';
import { ProgressStepper } from './ProgressStepper';
import { HomeScreen } from './screens/HomeScreen';
import { CheckoutUserScreen } from './screens/CheckoutUserScreen';
import { CheckoutConfirmScreen } from './screens/CheckoutConfirmScreen';
import { CheckinConfirmScreen } from './screens/CheckinConfirmScreen';
import { SuccessScreen } from './screens/SuccessScreen';
import { InfoMessageScreen } from './screens/InfoMessageScreen';

export function KioskLayout() {
  const { state } = useKiosk();
  const isOnline = useOnlineStatus();

  const isCheckin = state.screen === 'checkin-confirm'
    || (state.screen === 'success' && state.successMessage?.includes('returned'));

  function getStepNumber(): number {
    switch (state.screen) {
      case 'checkout-user':
      case 'checkin-confirm':
        return 0;
      case 'checkout-confirm':
        return 1;
      case 'success':
        return isCheckin ? 1 : 2;
      default:
        return -1;
    }
  }

  const stepNumber = getStepNumber();
  const showProgress = stepNumber >= 0;
  const stepLabels = isCheckin ? ['Confirm', 'Complete'] : ['Scan Badge', 'Confirm', 'Complete'];
  const modeColour = isCheckin ? 'green' : 'blue';

  function renderScreen() {
    switch (state.screen) {
      case 'checkout-user':
        return <CheckoutUserScreen />;
      case 'checkout-confirm':
        return <CheckoutConfirmScreen />;
      case 'checkin-confirm':
        return <CheckinConfirmScreen />;
      case 'success':
        return <SuccessScreen />;
      case 'info':
        return <InfoMessageScreen />;
      default:
        return <HomeScreen />;
    }
  }

  return (
    <Box
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: 'var(--bg-base)',
      }}
    >
      <Box
        component="header"
        style={{
          padding: 'var(--space-4) var(--space-6)',
          borderBottom: '1px solid var(--border-colour)',
          backgroundColor: 'var(--bg-card)',
          boxShadow: 'var(--shadow-xs)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Container size="lg">
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Box
                style={{
                  width: 4,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(180deg, var(--cmd-accent) 0%, rgba(59, 130, 246, 0.4) 100%)',
                  flexShrink: 0,
                }}
              />
              <Box>
                <Title
                  order={3}
                  fw={700}
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--text-primary)',
                    fontSize: '1.25rem',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}
                >
                  Commander
                </Title>
                <Text
                  size="xs"
                  fw={500}
                  style={{
                    fontFamily: 'var(--font-body)',
                    color: 'var(--text-muted)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    fontSize: '0.6875rem',
                  }}
                >
                  Equipment Kiosk{state.locationName ? ` — ${state.locationName}` : ''}
                </Text>
              </Box>
            </Group>

          </Group>
        </Container>
      </Box>

      {!isOnline && <OfflineBanner />}

      {showProgress && (
        <Box
          style={{
            borderBottom: '1px solid var(--border-colour)',
            padding: 'var(--space-4) var(--space-4)',
            backgroundColor: 'var(--bg-card)',
            position: 'relative',
            zIndex: 10,
            animation: 'kioskFadeIn var(--duration-normal) var(--ease-out)',
          }}
        >
          <Container size="md">
            <ProgressStepper
              steps={stepLabels}
              activeStep={stepNumber}
              colour={modeColour}
            />
          </Container>
        </Box>
      )}

      <Box
        component="main"
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: 'var(--space-8) var(--space-6) var(--space-6)',
          position: 'relative',
          zIndex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Container size="sm" style={{ width: '100%' }}>
          <Box
            key={state.screen}
            className="kiosk-screen-enter"
          >
            {renderScreen()}
          </Box>
        </Container>
      </Box>

      <Box
        component="footer"
        style={{
          padding: 'var(--space-3) var(--space-6)',
          borderTop: '1px solid var(--border-colour)',
          backgroundColor: 'var(--bg-card)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Container size="lg">
          <Group justify="center" gap={8}>
            <Box
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isOnline ? 'var(--green-accent)' : 'var(--amber-accent)',
                boxShadow: isOnline
                  ? '0 0 8px var(--cmd-success-glow)'
                  : '0 0 8px rgba(245, 158, 11, 0.45)',
                animation: 'kioskPulse 2.5s var(--ease-in-out) infinite',
              }}
            />
            <Text
              size="xs"
              fw={500}
              style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--text-muted)',
                fontSize: '0.75rem',
                letterSpacing: '0.03em',
              }}
            >
              {isOnline ? 'System Online' : 'System Offline'}
            </Text>
          </Group>
        </Container>
      </Box>
    </Box>
  );
}
